import * as THREE from "three";

export interface DecorColorSlot {
  id: string;
  /** Couleur d'origine, `#rrggbb`. */
  color: string;
  /** Part de la surface du modèle qui porte cette couleur. */
  share: number;
  /** Nom donné par le modèle (le pelage) ; absent, le panneau numérote les parties. */
  label?: string;
}

interface SlotSource extends DecorColorSlot {
  materials?: Set<string>;
  /**
   * Les couleurs regroupées dans la partie, en composantes linéaires, la plus étendue d'abord : des
   * nuances d'une même teinte, lues dans les couleurs de sommet ou dans l'image palette du matériau.
   */
  shades?: THREE.Color[];
  source?: "vertex" | "texture";
  /** Surface de la partie portée par chaque région du squelette (tête, torse, jambes…). */
  regions?: Map<BodyRegion, number>;
  /** Surface de la partie portée par chaque matériau, par son nom. */
  materialNames?: Map<string, number>;
  /** Surface de la partie portée par chaque morceau du modèle, par le mot qui finit son nom (`HeadGrey` → `Grey`). */
  nodeWords?: Map<string, number>;
}

type BodyRegion = "head" | "torso" | "hands" | "hips" | "legs" | "feet" | "tail";

const REGION_OF_BONE: [RegExp, BodyRegion][] = [
  [/head|ear|jaw|neck|hair/i, "head"],
  [/fist|hand|finger/i, "hands"],
  [/foot|toe|hoof/i, "feet"],
  [/tail/i, "tail"],
  [/hip|pelvis/i, "hips"],
  [/leg|thigh|knee|calf/i, "legs"],
  [/torso|abdomen|chest|spine|shoulder|arm|back|body/i, "torso"],
];

/** Les noms de matériaux parlants des modèles, traduits ; un nom qui n'est qu'une couleur n'en donne pas. */
const MATERIAL_LABELS: [RegExp, string][] = [
  [/^(body|livery)\s/i, "Carrosserie"],
  [/window/i, "Vitres"],
  [/glass/i, "Verre"],
  [/tire/i, "Pneus"],
  [/wheel/i, "Jantes"],
  [/headlight/i, "Phares"],
  [/rear light/i, "Feux arrière"],
  [/flasher|siren/i, "Gyrophares"],
  [/taxi/i, "Enseigne"],
  [/lea(f|v)/i, "Feuillage"],
  [/bark|trunk/i, "Tronc"],
  [/wood|deck|crate/i, "Bois"],
  [/stone/i, "Pierre"],
  [/concrete/i, "Béton"],
  [/canvas/i, "Toile"],
  [/carpet/i, "Tapis"],
  [/grass/i, "Herbe"],
  [/planter/i, "Bac"],
  [/plant|veg/i, "Plante"],
  [/soil/i, "Terre"],
  [/rope/i, "Corde"],
  [/screen/i, "Écran"],
  [/sign|label/i, "Inscriptions"],
  [/handle/i, "Poignées"],
  [/^bike$/i, "Cadre"],
  [/rubber/i, "Caoutchouc"],
  [/panel/i, "Panneaux"],
  [/trim|edge/i, "Bordure"],
  [/metal|steel|alu|pole|frame|truss/i, "Métal"],
];

/** Le début du nom d'un morceau rigide dit où il est sur le corps : `ArmRGreen`, `Tail2Dark`. */
const NODE_REGION = /^(head|neck|spine|torso|chest|body|arm|hand|hips|leg|foot|tail)(l|r)?\d*/i;
const REGION_OF_NODE: Record<string, BodyRegion> = { head: "head", neck: "head", spine: "torso", torso: "torso", chest: "torso", body: "torso", arm: "torso", hand: "hands", hips: "hips", leg: "legs", foot: "feet", tail: "tail" };
/** Les mots de fin de nom des morceaux qui disent ce qu'ils sont. */
const NODE_WORD_LABELS: [RegExp, string][] = [
  [/shirt/i, "T-shirt"],
  [/pant|trouser/i, "Pantalon"],
  [/shoe|boot/i, "Chaussures"],
  [/hair/i, "Cheveux"],
  [/teeth|tooth/i, "Dents"],
  [/logo/i, "Logo"],
];

/** Les parties qu'un matériau nomme quelle que soit la façon dont elles sont colorées. */
const PART_MATERIAL_LABELS: [RegExp, string][] = [[/mane/i, "Crinière"]];
const BIPED_LABELS: Partial<Record<BodyRegion, string>> = { head: "Cheveux", torso: "Haut", feet: "Chaussures", hands: "Mains" };
const ANIMAL_LABELS: Partial<Record<BodyRegion, string>> = { head: "Tête", legs: "Pattes", feet: "Pattes", tail: "Queue" };
/** Part de la partie qu'une région doit porter pour lui donner son nom. */
const OBVIOUS_SHARE = 0.75;

function isSkinTone(hex: string): boolean {
  const color = new THREE.Color(hex);
  const hsl = { h: 0, s: 0, l: 0 };
  color.getHSL(hsl, THREE.SRGBColorSpace);
  return hsl.h >= 0.03 && hsl.h <= 0.1 && hsl.s >= 0.25 && hsl.s <= 0.95 && hsl.l >= 0.25 && hsl.l <= 0.92;
}

/** Le nom d'une partie quand il va de soi : celui de son matériau, ou la région du corps qui la porte. */
function obviousLabel(slot: SlotSource, biped: boolean, skin: SlotSource | undefined, partNames: Record<string, string> = {}): string | undefined {
  const words = [...(slot.nodeWords ?? [])].sort((a, b) => b[1] - a[1]);
  const wordsTotal = words.reduce((sum, [, area]) => sum + area, 0);
  const word = words[0] && words[0][1] / wordsTotal >= OBVIOUS_SHARE ? words[0][0] : undefined;
  if (word) {
    const own = Object.entries(partNames).find(([key]) => key.toLowerCase() === word.toLowerCase());
    if (own) return own[1];
    const known = NODE_WORD_LABELS.find(([pattern]) => pattern.test(word));
    if (known) return known[1];
  }
  if (slot.id.startsWith("m:")) {
    const name = slot.id.slice(2);
    const hit = MATERIAL_LABELS.find(([pattern]) => pattern.test(name));
    if (hit) return hit[1];
  }
  const names = [...(slot.materialNames ?? [])].sort((a, b) => b[1] - a[1]);
  const named = names[0] && PART_MATERIAL_LABELS.find(([pattern]) => pattern.test(names[0]![0]));
  if (named) return named[1];
  if (!slot.regions?.size) return undefined;
  const total = [...slot.regions.values()].reduce((sum, area) => sum + area, 0);
  const share = (...regions: BodyRegion[]) => regions.reduce((sum, region) => sum + (slot.regions!.get(region) ?? 0), 0) / total;
  if (biped) {
    if (slot === skin) return "Peau";
    if (share("torso") >= 0.25 && share("hips", "legs") >= 0.25 && share("torso", "hips", "legs") >= OBVIOUS_SHARE) return "Tenue";
    if (share("hips", "legs") >= OBVIOUS_SHARE) return "Bas";
  }
  const labels = biped ? BIPED_LABELS : ANIMAL_LABELS;
  for (const [region, label] of Object.entries(labels) as [BodyRegion, string][]) if (share(region) >= OBVIOUS_SHARE) return label;
  if (!biped && share("torso", "hips", "legs", "feet", "tail") >= 0.6) return "Corps";
  return undefined;
}

/** Au-delà, les parties suivantes sont des détails : phares, yeux, liserés. */
const MAX_SLOTS = 5;
const MIN_SHARE = 0.04;
/** Une partie au nom évident reste proposée même petite : le sourire, les yeux. */
const MIN_NAMED_SHARE = 0.015;
const MAX_RECOLORED = 12;
/** En deçà, deux couleurs de sommet sont des nuances d'une même partie (écart en sRGB, sur 255). */
const SHADE_DISTANCE = 30;
/** Écart toléré entre une couleur de sommet, ou d'un point de l'image palette, et celle de la partie, en composantes linéaires. */
const VERTEX_COLOR_TOLERANCE = 0.006;
const TEXEL_COLOR_TOLERANCE = 0.015;

const texturePixels = new WeakMap<THREE.Texture, ImageData | null>();

/** Les pixels de l'image d'une texture, lus une fois ; `null` hors navigateur ou pour une image illisible. */
function pixelsOf(texture: THREE.Texture): ImageData | null {
  if (texturePixels.has(texture)) return texturePixels.get(texture)!;
  let pixels: ImageData | null = null;
  const image = texture.image as CanvasImageSource & { width?: number; height?: number } | undefined;
  try {
    if (image?.width && image.height) {
      const canvas = typeof OffscreenCanvas !== "undefined" ? new OffscreenCanvas(image.width, image.height) : typeof document !== "undefined" ? Object.assign(document.createElement("canvas"), { width: image.width, height: image.height }) : null;
      const context = canvas?.getContext("2d") as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null | undefined;
      if (context) {
        context.drawImage(image, 0, 0);
        pixels = context.getImageData(0, 0, image.width, image.height);
      }
    }
  } catch {
    pixels = null;
  }
  texturePixels.set(texture, pixels);
  return pixels;
}

/** Composantes telles qu'enregistrées dans le modèle, pour reconnaître une couleur de sommet. */
const rawHex = (color: THREE.Color) => [color.r, color.g, color.b].map((channel) => Math.round(channel * 255).toString(16).padStart(2, "0")).join("");

function triangleAreas(geometry: THREE.BufferGeometry, matrix: THREE.Matrix4, visit: (vertex: number, area: number, group: number, corners: [number, number, number]) => void): void {
  const position = geometry.getAttribute("position");
  if (!position) return;
  const index = geometry.index;
  const count = index ? index.count : position.count;
  const groups = geometry.groups.length ? geometry.groups : [{ start: 0, count, materialIndex: 0 }];
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const c = new THREE.Vector3();
  for (const group of groups) {
    for (let i = group.start; i + 2 < group.start + group.count; i += 3) {
      const ia = index ? index.getX(i) : i;
      const ib = index ? index.getX(i + 1) : i + 1;
      const ic = index ? index.getX(i + 2) : i + 2;
      a.fromBufferAttribute(position, ia).applyMatrix4(matrix);
      b.fromBufferAttribute(position, ib).applyMatrix4(matrix);
      c.fromBufferAttribute(position, ic).applyMatrix4(matrix);
      const area = b.sub(a).cross(c.sub(a)).length() / 2;
      if (area > 0) visit(ia, area, group.materialIndex ?? 0, [ia, ib, ic]);
    }
  }
}

/**
 * Les parties d'un modèle dont la couleur peut changer, de la plus étendue à la moins étendue :
 * un matériau sans texture, une couleur de sommet, une case d'image palette, ou les deux couleurs d'un pelage.
 */
export function collectColorSlots(root: THREE.Object3D, coat?: { light: string; dark: string; painted: string[]; labels: [string, string] }, skip: (node: THREE.Object3D) => boolean = () => false, partNames?: Record<string, string>): SlotSource[] {
  root.updateMatrixWorld(true);
  const inverseRoot = root.matrixWorld.clone().invert();
  const slots = new Map<string, SlotSource>();
  let total = 0;
  let region: BodyRegion | undefined;
  const add = (id: string, color: THREE.Color, area: number, extra: Partial<SlotSource>) => {
    let slot = slots.get(id);
    if (!slot) slots.set(id, (slot = { id, color: `#${color.getHexString()}`, share: 0, ...extra }));
    slot.share += area;
    if (region) (slot.regions ??= new Map()).set(region, (slot.regions.get(region) ?? 0) + area);
    if (materialName) (slot.materialNames ??= new Map()).set(materialName, (slot.materialNames.get(materialName) ?? 0) + area);
    if (nodeWord) (slot.nodeWords ??= new Map()).set(nodeWord, (slot.nodeWords.get(nodeWord) ?? 0) + area);
  };
  let nodeWord = "";
  let materialName = "";
  let biped = false;
  root.traverse((node) => {
    if (!(node instanceof THREE.Mesh) || skip(node)) return;
    const geometry = node.geometry as THREE.BufferGeometry;
    const bones = node instanceof THREE.SkinnedMesh ? node.skeleton.bones.map((bone) => REGION_OF_BONE.find(([pattern]) => pattern.test(bone.name))?.[1]) : null;
    if (node instanceof THREE.SkinnedMesh && node.skeleton.bones.some((bone) => /upperarm|forearm|lowerarm/i.test(bone.name))) biped = true;
    const joints = geometry.getAttribute("skinIndex");
    const weights = geometry.getAttribute("skinWeight");
    const nodeName = node.name && !/^mesh_?\d*$/i.test(node.name) ? node.name : node.parent?.name ?? "";
    const prefix = nodeName.match(NODE_REGION);
    const nodeRegion = prefix ? REGION_OF_NODE[prefix[1]!.toLowerCase()] : undefined;
    const word = prefix ? nodeName.slice(prefix[0].length) : "";
    const regionOf = (vertex: number): BodyRegion | undefined => {
      if (!bones || !joints || !weights) return nodeRegion;
      let best = 0;
      for (let k = 1; k < 4; k++) if (weights.getComponent(vertex, k) > weights.getComponent(vertex, best)) best = k;
      return bones[joints.getComponent(vertex, best)];
    };
    const materials: THREE.Material[] = Array.isArray(node.material) ? node.material : [node.material];
    const matrix = new THREE.Matrix4().multiplyMatrices(inverseRoot, node.matrixWorld);
    const vertexColors = geometry.getAttribute("color");
    const uvs = geometry.getAttribute("uv");
    const coloured = new THREE.Color();
    const uv = new THREE.Vector2();
    const corner = new THREE.Vector2();
    triangleAreas(geometry, matrix, (vertex, area, group, corners) => {
      total += area;
      region = regionOf(vertex);
      nodeWord = word;
      const material = materials[group] ?? materials[0];
      if (!material || !("color" in material) || !(material.color instanceof THREE.Color)) return;
      materialName = material.name;
      if (material.transparent && material.opacity < 0.9) return;
      const map = "map" in material && material.map instanceof THREE.Texture ? material.map : null;
      if (map) {
        const pixels = uvs ? pixelsOf(map) : null;
        if (!pixels) return;
        uv.set(0, 0);
        for (const index of corners) uv.add(corner.fromBufferAttribute(uvs as THREE.BufferAttribute, index));
        map.transformUv(uv.divideScalar(3));
        const x = Math.min(pixels.width - 1, Math.max(0, Math.floor(uv.x * pixels.width)));
        const y = Math.min(pixels.height - 1, Math.max(0, Math.floor(uv.y * pixels.height)));
        const offset = (y * pixels.width + x) * 4;
        const [red, green, blue] = [pixels.data[offset]!, pixels.data[offset + 1]!, pixels.data[offset + 2]!];
        const key = [red, green, blue].map((channel) => channel.toString(16).padStart(2, "0")).join("");
        coloured.setRGB(red / 255, green / 255, blue / 255, THREE.SRGBColorSpace);
        add(`t:${key}`, coloured.clone().multiply(material.color), area, { shades: [coloured.clone()], source: "texture" });
        return;
      }
      if (vertexColors && material.vertexColors) {
        coloured.fromBufferAttribute(vertexColors as THREE.BufferAttribute, vertex);
        const key = rawHex(coloured);
        if (coat?.painted.includes(key)) return;
        add(`v:${key}`, coloured.clone().multiply(material.color), area, { shades: [coloured.clone()], source: "vertex" });
        return;
      }
      const id = `m:${material.name || material.uuid}`;
      const slot = slots.get(id);
      if (slot) slot.materials!.add(material.uuid);
      add(id, material.color, area, { materials: slot?.materials ?? new Set([material.uuid]) });
    });
  });
  const merged: SlotSource[] = [];
  const srgb = (color: THREE.Color) => color.clone().convertLinearToSRGB();
  for (const slot of [...slots.values()].sort((a, b) => b.share - a.share)) {
    const main = slot.shades?.[0];
    const near = main && merged.find((other) => {
      const reference = other.shades?.[0];
      if (!reference || other.source !== slot.source) return false;
      const word = (part: SlotSource) => [...(part.nodeWords ?? [])].sort((a, b) => b[1] - a[1])[0]?.[0];
      if (word(slot) && word(other) && word(slot) !== word(other)) return false;
      const x = srgb(main), y = srgb(reference);
      return Math.hypot(x.r - y.r, x.g - y.g, x.b - y.b) * 255 < SHADE_DISTANCE;
    });
    if (near) {
      near.shades!.push(main);
      near.share += slot.share;
      for (const [part, area] of slot.regions ?? []) (near.regions ??= new Map()).set(part, (near.regions.get(part) ?? 0) + area);
      for (const [name, area] of slot.materialNames ?? []) (near.materialNames ??= new Map()).set(name, (near.materialNames.get(name) ?? 0) + area);
      for (const [name, area] of slot.nodeWords ?? []) (near.nodeWords ??= new Map()).set(name, (near.nodeWords.get(name) ?? 0) + area);
    } else merged.push(slot);
  }
  const ranked = merged
    .map((slot) => ({ ...slot, share: total ? slot.share / total : 0 }))
    .filter((slot) => slot.share >= MIN_NAMED_SHARE)
    .sort((a, b) => b.share - a.share);
  const coatSlots: SlotSource[] = coat
    ? [
        { id: "coat:light", color: coat.light, share: 1, label: coat.labels[0] },
        { id: "coat:dark", color: coat.dark, share: 1, label: coat.labels[1] },
      ]
    : [];
  const kept = [...coatSlots, ...ranked];
  // Des cheveux bruns ont la teinte d'une peau mate : la peau est celle qu'on trouve aussi sur les mains.
  const handsShare = (slot: SlotSource) => {
    const total = [...(slot.regions?.values() ?? [])].reduce((sum, area) => sum + area, 0);
    return total ? (slot.regions!.get("hands") ?? 0) / total : 0;
  };
  const skin = biped ? kept.filter((slot) => !slot.label && isSkinTone(slot.color) && handsShare(slot) > 0.02).sort((a, b) => handsShare(b) - handsShare(a))[0] : undefined;
  const taken = new Map<string, number>();
  for (const slot of kept) {
    if (slot.label) continue;
    const label = obviousLabel(slot, biped, skin, partNames);
    if (!label || (label === "Corps" && taken.has(label))) continue;
    const count = (taken.get(label) ?? 0) + 1;
    taken.set(label, count);
    slot.label = count > 1 ? `${label} ${count}` : label;
  }
  return kept.filter((slot) => slot.label || slot.share >= MIN_SHARE).slice(0, MAX_SLOTS);
}

const RECOLOR_FRAGMENT_HEAD = /* glsl */ `
  uniform vec3 uRecolorFrom[${MAX_RECOLORED}];
  uniform vec3 uRecolorTo[${MAX_RECOLORED}];
  uniform int uRecolorCount;
  uniform vec3 uTexelFrom[${MAX_RECOLORED}];
  uniform vec3 uTexelTo[${MAX_RECOLORED}];
  uniform int uTexelCount;
`;

const RECOLOR_MAP_BODY = /* glsl */ `
  #include <map_fragment>
  #ifdef USE_MAP
    for (int i = 0; i < ${MAX_RECOLORED}; i++) {
      if (i >= uTexelCount) break;
      vec3 texelGap = abs(sampledDiffuseColor.rgb - uTexelFrom[i]);
      if (max(texelGap.r, max(texelGap.g, texelGap.b)) < ${TEXEL_COLOR_TOLERANCE.toFixed(4)}) diffuseColor.rgb = diffuse * uTexelTo[i];
    }
  #endif
`;

const RECOLOR_FRAGMENT_BODY = /* glsl */ `
  #include <color_fragment>
  #if defined( USE_COLOR ) || defined( USE_COLOR_ALPHA )
    for (int i = 0; i < ${MAX_RECOLORED}; i++) {
      if (i >= uRecolorCount) break;
      vec3 gap = abs(vColor.rgb - uRecolorFrom[i]);
      if (max(gap.r, max(gap.g, gap.b)) < ${VERTEX_COLOR_TOLERANCE.toFixed(4)}) diffuseColor.rgb = diffuse * uRecolorTo[i];
    }
  #endif
`;

/** Une copie de matériau garde le vent, le pelage et les couleurs que porte l'original. */
export function withShader<T extends THREE.Material>(copy: T, original: THREE.Material): T {
  copy.onBeforeCompile = original.onBeforeCompile;
  copy.customProgramCacheKey = original.customProgramCacheKey;
  for (const key of ["coatUniforms", "recolorUniforms"]) if (original.userData[key]) copy.userData[key] = original.userData[key];
  return copy;
}

/** Le matériau d'une partie aux couleurs choisies ; l'original quand aucune ne le concerne. */
export function recolorMaterial(material: THREE.Material, slots: readonly SlotSource[], colors: Record<string, string>): THREE.Material {
  const chosen = slots.filter((slot) => colors[slot.id]);
  const own = chosen.filter((slot) => slot.materials?.has(material.uuid));
  const map = "map" in material && material.map instanceof THREE.Texture ? material.map : null;
  const vertex = chosen.filter((slot) => slot.source === "vertex" && material.vertexColors && !map);
  const texel = chosen.filter((slot) => slot.source === "texture" && !!map);
  const coat = material.userData.coatUniforms ? chosen.filter((slot) => slot.id.startsWith("coat:")) : [];
  if (!own.length && !vertex.length && !texel.length && !coat.length) return material;

  const copy = withShader(material.clone(), material);
  for (const slot of own) if ("color" in copy && copy.color instanceof THREE.Color) copy.color.set(colors[slot.id]!);
  if (coat.length) {
    const base = material.userData.coatUniforms as Record<string, { value: THREE.Color }>;
    copy.userData.coatUniforms = {
      uCoatLight: { value: colors["coat:light"] ? new THREE.Color(colors["coat:light"]) : base.uCoatLight!.value },
      uCoatDark: { value: colors["coat:dark"] ? new THREE.Color(colors["coat:dark"]) : base.uCoatDark!.value },
    };
  }
  if (vertex.length || texel.length) {
    const tint = "color" in material && material.color instanceof THREE.Color ? material.color : new THREE.Color(1, 1, 1);
    // Chaque nuance garde son écart de clarté avec la couleur principale de sa partie.
    const pairsOf = (slots: SlotSource[]) => slots.flatMap((slot) => {
      const main = slot.shades![0]!;
      const target = new THREE.Color(colors[slot.id]!);
      return slot.shades!.map((shade) => ({
        from: new THREE.Vector3(shade.r, shade.g, shade.b),
        to: new THREE.Vector3(
          (target.r * (shade.r / Math.max(main.r, 1e-3))) / Math.max(tint.r, 1e-3),
          (target.g * (shade.g / Math.max(main.g, 1e-3))) / Math.max(tint.g, 1e-3),
          (target.b * (shade.b / Math.max(main.b, 1e-3))) / Math.max(tint.b, 1e-3),
        ),
      }));
    }).slice(0, MAX_RECOLORED);
    const vertexPairs = pairsOf(vertex);
    const texelPairs = pairsOf(texel);
    const fill = (pairs: { from: THREE.Vector3; to: THREE.Vector3 }[], key: "from" | "to") => Array.from({ length: MAX_RECOLORED }, (_, i) => pairs[i]?.[key] ?? new THREE.Vector3());
    copy.userData.recolorUniforms = {
      uRecolorFrom: { value: fill(vertexPairs, "from") },
      uRecolorTo: { value: fill(vertexPairs, "to") },
      uRecolorCount: { value: vertexPairs.length },
      uTexelFrom: { value: fill(texelPairs, "from") },
      uTexelTo: { value: fill(texelPairs, "to") },
      uTexelCount: { value: texelPairs.length },
    };
    const previous = material.onBeforeCompile;
    const previousKey = material.customProgramCacheKey.bind(material);
    copy.onBeforeCompile = function (this: THREE.Material, shader, renderer) {
      previous.call(this, shader, renderer);
      Object.assign(shader.uniforms, this.userData.recolorUniforms);
      shader.fragmentShader = shader.fragmentShader
        .replace("#include <common>", `#include <common>\n${RECOLOR_FRAGMENT_HEAD}`)
        .replace("#include <map_fragment>", RECOLOR_MAP_BODY)
        .replace("#include <color_fragment>", RECOLOR_FRAGMENT_BODY);
    };
    copy.customProgramCacheKey = () => `${previousKey()}|recolor`;
  }
  copy.needsUpdate = true;
  return copy;
}

/** La signature des couleurs choisies, pour reconnaître deux objets aux mêmes couleurs. */
export function colorsSignature(colors: Record<string, string> | undefined): string {
  if (!colors) return "";
  return Object.keys(colors).sort().map((slot) => `${slot}=${colors[slot]}`).join(",");
}
